import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';
import { jwtDecode } from 'jwt-decode';
import {
  Avatar,
  Button,
  TextField,
  Box,
  Typography,
  Container,
  Alert,
  Link,
  CircularProgress,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';

const Reset = () => {
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { resetPassword } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      if (tokenFromUrl) {
        try {
          // Appel à l'API pour une vérification sécurisée du token
          await axiosInstance.post('/auth/verify-reset-token', { token: tokenFromUrl });
          setToken(tokenFromUrl);
          setIsTokenValid(true);
        } catch (e) {
          setError("Le lien de réinitialisation est invalide ou a expiré.");
          setIsTokenValid(false);
        }
      } else {
        setError("Token de réinitialisation manquant.");
        setIsTokenValid(false);
      }
      setIsLoading(false); // La vérification est terminée
    };

    verifyToken();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!password || !confirmPassword) {
      setError('Veuillez remplir tous les champs.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    try {
      await resetPassword(token, password);
      setSuccess('Votre mot de passe a été réinitialisé avec succès. Vous allez être redirigé vers la page de connexion.');
      setTimeout(() => {
        navigate('/login');
      }, 4000);
    } catch (error) {
      const errorCode = error.response?.data?.error;
      switch (errorCode) {
        case 'INVALID_TOKEN':
          setError('Le lien de réinitialisation est invalide ou a expiré.');
          break;
        case 'WEAK_PASSWORD':
          setError('Le mot de passe est trop faible. Il doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre.');
          break;
        case 'USER_NOT_FOUND':
          setError("L'utilisateur associé à ce token n'a pas été trouvé.");
          break;
        default:
          setError('Une erreur est survenue. Veuillez réessayer.');
      }
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockResetIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Réinitialiser le mot de passe
        </Typography>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3, width: '100%' }}>
            {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{success}</Alert>}

            {!success && (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Nouveau mot de passe"
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={!isTokenValid}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirmer le mot de passe"
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={!isTokenValid}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={!isTokenValid}
                >
                  Réinitialiser
                </Button>
              </>
            )}
          </Box>
        )}
        {!isLoading && !isTokenValid && (
          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            <Link component={RouterLink} to="/login" variant="body2">
              Retour à la page de connexion
            </Link>
          </Typography>
        )}
      </Box>
    </Container>
  );
};

export default Reset;